// The Meta-approved WhatsApp templates.
//
// Like the SMS wordings, nothing here is composed: each component is a
// transcription of text Meta has already approved through MSG91. Header, body,
// footer and buttons are edited separately because Meta approves them as
// separate components with their own {{n}} numbering, and every outgoing
// message is rebuilt from exactly what is stored here. This screen therefore
// never trims, re-spaces or re-cases anything typed into it.
import { useMemo, useState } from 'react';
import { MessageSquareText, Plus, Trash2 } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Input, NativeSelect, Textarea } from '@/components/ui/Input';
import { Switch } from '@/components/ui/Switch';
import { FormField } from '@/components/ui/FormField';
import { LoadingState, ErrorState, EmptyState } from '@/components/ui/States';
import { ApiError } from '@/lib/api/client';
import { previewTemplate, testSend } from './api';
import { useWhatsAppTemplates, useSaveWhatsAppTemplate } from './hooks';
import { countsOf, fill, resizeValues } from './waTemplate';
import type {
  WhatsAppTemplate, WhatsAppComponents, WhatsAppButton, WhatsAppValues,
  WhatsAppPreview, WhatsAppTestSendResult, WhatsAppStatus,
} from './types';

const EXACT_HELP = 'Paste the WhatsApp template text EXACTLY as approved in MSG91/Meta.';

const STATUSES: { value: WhatsAppStatus; label: string }[] = [
  { value: 'draft', label: 'Draft' },
  { value: 'submitted', label: 'Submitted to Meta' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'paused', label: 'Paused by Meta' },
  { value: 'disabled', label: 'Disabled by Meta' },
];

interface Draft {
  id: number;
  template_key: string;
  name: string;
  whatsapp_template_name: string;
  whatsapp_language_code: string;
  whatsapp_namespace: string;
  whatsapp_category: 'utility' | 'authentication';
  whatsapp_status: WhatsAppStatus;
  active: boolean;
  notes: string;
  hasHeader: boolean;
  header: string;
  body: string;
  footer: string;
  buttons: WhatsAppButton[];
}

const BLANK: Draft = {
  id: 0, template_key: '', name: '', whatsapp_template_name: '', whatsapp_language_code: 'en',
  whatsapp_namespace: '', whatsapp_category: 'utility', whatsapp_status: 'draft', active: false, notes: '',
  hasHeader: false, header: '', body: '', footer: '', buttons: [],
};

function toDraft(t: WhatsAppTemplate): Draft {
  const c = t.whatsapp_components ?? { body: { text: '' } };
  return {
    id: t.id,
    template_key: t.template_key,
    name: t.name,
    whatsapp_template_name: t.whatsapp_template_name ?? '',
    whatsapp_language_code: t.whatsapp_language_code ?? '',
    whatsapp_namespace: t.whatsapp_namespace ?? '',
    whatsapp_category: t.whatsapp_category,
    whatsapp_status: t.whatsapp_status,
    active: t.active,
    notes: t.notes ?? '',
    hasHeader: !!c.header,
    header: c.header?.text ?? '',
    body: c.body?.text ?? '',
    footer: c.footer?.text ?? '',
    buttons: c.buttons ?? [],
  };
}

function componentsOf(d: Draft): WhatsAppComponents {
  return {
    header: d.hasHeader ? { type: 'text', text: d.header } : null,
    body: { text: d.body },
    // An empty footer box means "no footer" — absence, not an edit of text.
    footer: d.footer !== '' ? { text: d.footer } : null,
    buttons: d.buttons,
  };
}

function statusBadge(t: WhatsAppTemplate) {
  if (t.ready) return <Badge variant="success">Ready to send</Badge>;
  if (t.whatsapp_status === 'paused' || t.whatsapp_status === 'disabled' || t.whatsapp_status === 'rejected') {
    return <Badge variant="danger">{STATUSES.find((s) => s.value === t.whatsapp_status)?.label}</Badge>;
  }
  if (!t.active && t.whatsapp_status === 'approved') return <Badge variant="neutral">Switched off</Badge>;
  return <Badge variant="warn">{STATUSES.find((s) => s.value === t.whatsapp_status)?.label ?? t.whatsapp_status}</Badge>;
}

const VarCount = ({ n }: { n: number }) => (
  <span className="text-[11px] text-ink-muted">
    <strong>{n}</strong> variable{n === 1 ? '' : 's'}{n ? ` ({{1}}${n > 1 ? `…{{${n}}}` : ''})` : ''}
  </span>
);

export default function TemplatesTab() {
  const templates = useWhatsAppTemplates();
  const save = useSaveWhatsAppTemplate();

  const [editing, setEditing] = useState<Draft | null>(null);
  const [values, setValues] = useState<WhatsAppValues>({ header: [], body: [], buttons: [] });
  const [preview, setPreview] = useState<WhatsAppPreview | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [mobile, setMobile] = useState('');
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState<WhatsAppTestSendResult | null>(null);
  const [sendError, setSendError] = useState<string | null>(null);

  const components = useMemo(() => (editing ? componentsOf(editing) : null), [editing]);
  const counts = useMemo(() => (components ? countsOf(components) : { header: 0, body: 0, buttons: [] }), [components]);
  // Kept in step with the components so the sample boxes always match the
  // variables actually present.
  const sized = useMemo(() => resizeValues(values, counts), [values, counts]);

  function open(t: WhatsAppTemplate | null) {
    setEditing(t ? toDraft(t) : { ...BLANK });
    setValues({ header: [], body: [], buttons: [] });
    setPreview(null);
    setSendResult(null);
    setSendError(null);
    setMobile('');
  }

  function edit<K extends keyof Draft>(k: K, v: Draft[K]) {
    setEditing((p) => (p ? { ...p, [k]: v } : p));
    setPreview(null);
  }

  function editButton(i: number, next: WhatsAppButton) {
    setEditing((p) => (p ? { ...p, buttons: p.buttons.map((b, j) => (j === i ? next : b)) } : p));
    setPreview(null);
  }

  function setButtonType(i: number, type: WhatsAppButton['type']) {
    const text = editing?.buttons[i]?.text ?? '';
    if (type === 'url') editButton(i, { type, text, url: 'https://' });
    else if (type === 'phone_number') editButton(i, { type, text, phone_number: '' });
    else editButton(i, { type, text });
  }

  async function onSave() {
    if (!editing || !components) return;
    const data = {
      ...(editing.id ? {} : { template_key: editing.template_key }),
      name: editing.name,
      whatsapp_template_name: editing.whatsapp_template_name || null,
      whatsapp_language_code: editing.whatsapp_language_code || null,
      whatsapp_namespace: editing.whatsapp_namespace || null,
      whatsapp_category: editing.whatsapp_category,
      whatsapp_status: editing.whatsapp_status,
      active: editing.active,
      notes: editing.notes || null,
      whatsapp_components: components,
    };
    const saved = await save.mutateAsync({ id: editing.id || undefined, data });
    setEditing(toDraft(saved));
  }

  async function runPreview() {
    if (!editing?.id) return;
    setPreviewing(true);
    setPreview(null);
    try {
      setPreview(await previewTemplate(editing.id, sized));
    } catch (e) {
      setPreview({ valid: false, error: e instanceof ApiError ? e.message : 'Could not build a preview.' });
    } finally {
      setPreviewing(false);
    }
  }

  async function runTestSend() {
    if (!editing?.id) return;
    setSending(true);
    setSendResult(null);
    setSendError(null);
    try {
      setSendResult(await testSend(editing.id, mobile, sized));
    } catch (e) {
      setSendError(e instanceof ApiError ? e.message : 'The test send could not be run.');
    } finally {
      setSending(false);
    }
  }

  if (templates.isLoading) return <LoadingState label="Loading WhatsApp templates…" />;
  if (templates.isError) {
    return (
      <ErrorState
        message={templates.error instanceof ApiError ? templates.error.message : 'Could not load WhatsApp templates'}
        onRetry={() => templates.refetch()}
      />
    );
  }

  const rows = templates.data ?? [];
  const fieldErr = (k: string) => (save.error instanceof ApiError ? save.error.fieldErrors[k] : undefined);

  // ── Editor ───────────────────────────────────────────────────
  if (editing && components) {
    const stored = editing.id ? rows.find((r) => r.id === editing.id) : undefined;
    const setVal = (part: 'header' | 'body', i: number, v: string) =>
      setValues({ ...sized, [part]: sized[part].map((x, j) => (j === i ? v : x)) });
    const setBtnVal = (bi: number, i: number, v: string) =>
      setValues({ ...sized, buttons: sized.buttons.map((arr, j) => (j === bi ? arr.map((x, k) => (k === i ? v : x)) : arr)) });

    return (
      <div className="max-w-3xl space-y-4">
        <button onClick={() => setEditing(null)} className="text-xs font-medium text-teal hover:underline">
          ‹ Back to templates
        </button>

        <Card>
          <CardHeader>
            <CardTitle>{editing.id ? editing.name : 'New WhatsApp template'}</CardTitle>
            {stored && statusBadge(stored)}
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid2">
              <FormField label="Name" required error={fieldErr('name')}>
                <Input value={editing.name} onChange={(e) => edit('name', e.target.value)} placeholder="Appointment confirmed" />
              </FormField>
              <FormField label="Event key" required error={fieldErr('template_key')}>
                <Input
                  value={editing.template_key}
                  onChange={(e) => edit('template_key', e.target.value)}
                  placeholder="appointment_booked"
                  disabled={!!editing.id}
                />
                <div className="mt-1 text-[11px] text-ink-muted">
                  {editing.id ? 'Fixed once created — delivery log rows refer to it.' : 'The same key the SMS wording for this event uses.'}
                </div>
              </FormField>
              <FormField label="Meta template name" error={fieldErr('whatsapp_template_name')}>
                <Input
                  value={editing.whatsapp_template_name}
                  onChange={(e) => edit('whatsapp_template_name', e.target.value)}
                  placeholder="appointment_confirmed_en"
                  className="font-mono text-[12px]"
                />
              </FormField>
              <FormField label="Language code" error={fieldErr('whatsapp_language_code')}>
                <Input
                  value={editing.whatsapp_language_code}
                  onChange={(e) => edit('whatsapp_language_code', e.target.value)}
                  placeholder="en"
                  className="font-mono text-[12px]"
                />
              </FormField>
              <FormField label="Namespace (if MSG91 shows one)">
                <Input
                  value={editing.whatsapp_namespace}
                  onChange={(e) => edit('whatsapp_namespace', e.target.value)}
                  className="font-mono text-[12px]"
                />
              </FormField>
              <FormField label="Category" error={fieldErr('whatsapp_category')}>
                <NativeSelect
                  value={editing.whatsapp_category}
                  onChange={(e) => edit('whatsapp_category', e.target.value as Draft['whatsapp_category'])}
                >
                  <option value="utility">Utility</option>
                  <option value="authentication">Authentication</option>
                </NativeSelect>
                <div className="mt-1 text-[11px] text-ink-muted">Marketing templates are not accepted.</div>
              </FormField>
            </div>

            <div className="rounded-xl border border-warn-soft bg-warn-soft p-3 text-[12px] text-warn-ink">
              <strong>{EXACT_HELP}</strong> Every message is rebuilt from what is saved here. Whitespace, punctuation,
              capitalisation and the <code>{'{{n}}'}</code> variables are kept exactly as typed — nothing is tidied up.
              Variables are numbered separately in each component, starting at <code>{'{{1}}'}</code>.
            </div>

            {fieldErr('whatsapp_components') && (
              <div className="rounded-xl border border-danger-soft bg-danger-soft p-3 text-[12px] text-danger-ink">
                ✗ {fieldErr('whatsapp_components')}
              </div>
            )}

            {/* Header */}
            <div className="space-y-2 border-t border-border pt-4">
              <label className="flex items-center justify-between gap-4">
                <div className="text-[13px] font-semibold">Header</div>
                <Switch checked={editing.hasHeader} onCheckedChange={(v) => edit('hasHeader', v)} aria-label="Has header" />
              </label>
              {editing.hasHeader && (
                <FormField label="Header text (at most one variable)">
                  <Input value={editing.header} onChange={(e) => edit('header', e.target.value)} className="font-mono text-[12px]" />
                  <div className="mt-1"><VarCount n={counts.header} /></div>
                </FormField>
              )}
            </div>

            {/* Body */}
            <FormField label="Body" required>
              <Textarea
                value={editing.body}
                onChange={(e) => edit('body', e.target.value)}
                rows={5}
                className="font-mono text-[12px]"
                placeholder="Dear {{1}}, your appointment with {{2}} at {{3}} is confirmed for {{4}} at {{5}}."
              />
              <div className="mt-1 flex justify-between">
                <VarCount n={counts.body} />
                <span className="text-[11px] text-ink-muted">{editing.body.length} / 1024</span>
              </div>
            </FormField>

            {/* Footer */}
            <FormField label="Footer (optional, static text only)">
              <Input value={editing.footer} onChange={(e) => edit('footer', e.target.value)} className="font-mono text-[12px]" />
              {/\{\{/.test(editing.footer) && (
                <div className="mt-1 text-[11px] text-danger-ink">A footer cannot contain a variable — WhatsApp rejects it.</div>
              )}
            </FormField>

            {/* Buttons */}
            <div className="space-y-2 border-t border-border pt-4">
              <div className="flex items-center justify-between">
                <div className="text-[13px] font-semibold">Buttons</div>
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={editing.buttons.length >= 10}
                  onClick={() => edit('buttons', [...editing.buttons, { type: 'quick_reply', text: '' }])}
                >
                  <Plus size={14} /> Add button
                </Button>
              </div>
              {editing.buttons.length === 0 && <div className="text-[12px] text-ink-muted">No buttons.</div>}
              {editing.buttons.map((b, i) => (
                <div key={i} className="grid2 rounded-xl border border-border p-3">
                  <FormField label={`Button ${i + 1} type`}>
                    <NativeSelect value={b.type} onChange={(e) => setButtonType(i, e.target.value as WhatsAppButton['type'])}>
                      <option value="quick_reply">Quick reply</option>
                      <option value="url">Open a link</option>
                      <option value="phone_number">Call a number</option>
                    </NativeSelect>
                  </FormField>
                  <FormField label="Label">
                    <Input value={b.text} onChange={(e) => editButton(i, { ...b, text: e.target.value })} />
                  </FormField>
                  {b.type === 'url' && (
                    <FormField label="URL (one variable allowed, at the end)">
                      <Input value={b.url} onChange={(e) => editButton(i, { ...b, url: e.target.value })} className="font-mono text-[12px]" />
                      <div className="mt-1"><VarCount n={counts.buttons[i] ?? 0} /></div>
                    </FormField>
                  )}
                  {b.type === 'phone_number' && (
                    <FormField label="Phone number">
                      <Input value={b.phone_number} onChange={(e) => editButton(i, { ...b, phone_number: e.target.value })} placeholder="+914400000000" />
                    </FormField>
                  )}
                  <div className="flex items-end">
                    <Button size="sm" variant="ghost" onClick={() => edit('buttons', editing.buttons.filter((_, j) => j !== i))}>
                      <Trash2 size={14} /> Remove
                    </Button>
                  </div>
                </div>
              ))}
            </div>

            {/* Live preview — client-side, as typed */}
            <div className="rounded-xl border border-border bg-muted p-3">
              <div className="text-[11px] font-semibold uppercase tracking-wide text-ink-muted">Live preview (as typed, with the sample values below)</div>
              {editing.hasHeader && editing.header && (
                <div className="mt-1.5 whitespace-pre-wrap text-[13px] font-semibold">{fill(editing.header, sized.header)}</div>
              )}
              <div className="mt-1.5 whitespace-pre-wrap text-[12px]">{fill(editing.body, sized.body) || '—'}</div>
              {editing.footer && <div className="mt-1.5 whitespace-pre-wrap text-[11px] text-ink-muted">{editing.footer}</div>}
              {editing.buttons.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {editing.buttons.map((b, i) => (
                    <Badge key={i} variant="neutral">
                      {b.text || `Button ${i + 1}`}{b.type === 'url' ? ` → ${fill(b.url, sized.buttons[i] ?? [])}` : ''}
                    </Badge>
                  ))}
                </div>
              )}
            </div>

            <div className="grid2 border-t border-border pt-4">
              <FormField label="Meta status" error={fieldErr('whatsapp_status')}>
                <NativeSelect value={editing.whatsapp_status} onChange={(e) => edit('whatsapp_status', e.target.value as WhatsAppStatus)}>
                  {STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                </NativeSelect>
                <div className="mt-1 text-[11px] text-ink-muted">
                  Keep this in step with MSG91. Meta can pause or disable an approved template later; only Approved is sent.
                </div>
              </FormField>
              <FormField label="Notes">
                <Input value={editing.notes} onChange={(e) => edit('notes', e.target.value)} />
              </FormField>
            </div>

            <label className="flex items-center justify-between gap-4 border-t border-border pt-4">
              <div>
                <div className="text-[13px] font-semibold">In use</div>
                <div className="mt-0.5 text-[12px] text-ink-muted">
                  Can only be switched on once the status is Approved.
                </div>
                {fieldErr('active') && <div className="mt-1 text-[11px] text-danger-ink">{fieldErr('active')}</div>}
              </div>
              <Switch checked={editing.active} onCheckedChange={(v) => edit('active', v)} aria-label="In use" />
            </label>

            {save.isError && !(save.error instanceof ApiError && Object.keys(save.error.fieldErrors).length) && (
              <div className="rounded-xl border border-danger-soft bg-danger-soft p-3 text-[13px] text-danger-ink">
                ✗ {save.error instanceof ApiError ? save.error.message : 'Could not save.'}
              </div>
            )}

            {stored && stored.blockers.length > 0 && (
              <ul className="list-disc space-y-1 pl-4 text-[11px] text-warn-ink">
                {stored.blockers.map((b) => <li key={b.code}>{b.message}</li>)}
              </ul>
            )}

            <Button onClick={onSave} loading={save.isPending}>Save template</Button>
          </CardContent>
        </Card>

        {!!editing.id && (
          <Card>
            <CardHeader>
              <CardTitle>Check this template</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="text-[12px] text-ink-muted">
                Validate builds the message on the server from the <strong>saved</strong> components, exactly as a real
                send would — save your edits first. Nothing is sent by validating.
              </div>

              {counts.header > 0 && sized.header.map((v, i) => (
                <FormField key={`h${i}`} label={`Header {{${i + 1}}}`}>
                  <Input value={v} onChange={(e) => setVal('header', i, e.target.value)} />
                </FormField>
              ))}
              {counts.body > 0 && (
                <div className="grid2">
                  {sized.body.map((v, i) => (
                    <FormField key={`b${i}`} label={`Body {{${i + 1}}}`}>
                      <Input value={v} onChange={(e) => setVal('body', i, e.target.value)} />
                    </FormField>
                  ))}
                </div>
              )}
              {sized.buttons.map((arr, bi) => arr.map((v, i) => (
                <FormField key={`btn${bi}-${i}`} label={`Button ${bi + 1} URL {{${i + 1}}}`}>
                  <Input value={v} onChange={(e) => setBtnVal(bi, i, e.target.value)} />
                </FormField>
              )))}

              <Button variant="ghost" onClick={runPreview} loading={previewing}>Validate</Button>

              {preview && (
                <div className={'rounded-xl border p-3 ' + (preview.valid ? 'border-border bg-muted' : 'border-danger-soft bg-danger-soft')}>
                  {preview.valid ? (
                    <>
                      <div className="text-[11px] font-semibold uppercase tracking-wide text-ink-muted">As the patient would read it</div>
                      <div className="mt-1.5 whitespace-pre-wrap text-[12px]">{preview.text}</div>
                      <div className="mt-3"><Badge variant="success">Template text verified</Badge></div>
                      {!!preview.blockers?.length && (
                        <ul className="mt-2 list-disc space-y-1 pl-4 text-[11px] text-warn-ink">
                          {preview.blockers.map((b) => <li key={b.code}>{b.message}</li>)}
                        </ul>
                      )}
                    </>
                  ) : (
                    <div className="text-[12px] text-danger-ink">✗ {preview.error}</div>
                  )}
                </div>
              )}

              <div className="border-t border-border pt-4">
                <div className="text-[13px] font-semibold">Send a test to a staff phone</div>
                <div className="mt-0.5 mb-2.5 text-[12px] text-ink-muted">
                  Goes through the same path a real send does, except the patient consent check (there is no patient).
                  While <strong>Simulate only</strong> is on this comes back <code>simulated</code>. Always logged.
                </div>
                <div className="flex flex-wrap items-end gap-2">
                  <FormField label="Staff mobile number" className="w-56">
                    <Input value={mobile} onChange={(e) => setMobile(e.target.value)} placeholder="9876543210" />
                  </FormField>
                  <Button variant="ghost" onClick={runTestSend} loading={sending} disabled={!mobile}>Send test</Button>
                </div>

                {sendError && (
                  <div className="mt-3 rounded-xl border border-danger-soft bg-danger-soft p-3 text-[12px] text-danger-ink">✗ {sendError}</div>
                )}
                {sendResult && (
                  <div
                    className={
                      'mt-3 rounded-xl border p-3 text-[12px] ' +
                      (sendResult.status === 'sent'
                        ? 'border-success-soft bg-success-soft text-success-ink'
                        : sendResult.status === 'simulated'
                          ? 'border-warn-soft bg-warn-soft text-warn-ink'
                          : 'border-danger-soft bg-danger-soft text-danger-ink')
                    }
                  >
                    <div className="font-semibold">
                      {sendResult.status === 'sent'
                        ? `Sent to ${sendResult.recipient}`
                        : sendResult.status === 'simulated' ? 'Simulated — nothing was sent or charged' : 'Not sent'}
                    </div>
                    {sendResult.failure_reason && <div className="mt-1">{sendResult.failure_reason}</div>}
                    {sendResult.message_text && (
                      <div className="mt-2 whitespace-pre-wrap text-[11px] opacity-90">{sendResult.message_text}</div>
                    )}
                    {sendResult.provider_message_id && (
                      <div className="mt-1 text-[11px] opacity-80">MSG91 reference {sendResult.provider_message_id}</div>
                    )}
                    <div className="mt-1 text-[11px] opacity-80">Recorded in the delivery log either way.</div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    );
  }

  // ── List ─────────────────────────────────────────────────────
  return (
    <div>
      <div className="mb-4 text-[13px] leading-relaxed text-ink-muted">
        The WhatsApp templates this hospital may send. Each one must be approved by Meta through MSG91 first, then
        entered here exactly as approved, marked Approved and switched on. Anything else is built and logged but never
        delivered.
      </div>

      <Card>
        <CardHeader>
          <CardTitle><MessageSquareText size={15} /> WhatsApp templates</CardTitle>
          <Button size="sm" variant="ghost" onClick={() => open(null)}>
            <Plus size={14} /> New template
          </Button>
        </CardHeader>

        {rows.length === 0 ? (
          <EmptyState label="No WhatsApp templates yet" hint={EXACT_HELP} />
        ) : (
          <div className="divide-y divide-border">
            {rows.map((t) => (
              <button
                key={t.id}
                onClick={() => open(t)}
                className="flex w-full items-start justify-between gap-4 p-4 text-left hover:bg-muted"
              >
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-[13px] font-semibold">{t.name}</span>
                    <code className="text-[11px] text-ink-muted">{t.template_key}</code>
                    {statusBadge(t)}
                  </div>
                  <div className="mt-1 truncate font-mono text-[11px] text-ink-muted">
                    {t.whatsapp_template_name ?? '—'} · {t.whatsapp_components?.body?.text ?? ''}
                  </div>
                  {t.blockers[0] && <div className="mt-1 text-[11px] text-warn-ink">{t.blockers[0].message}</div>}
                </div>
                <div className="shrink-0 text-[11px] text-ink-muted">
                  {t.variable_count} variable{t.variable_count === 1 ? '' : 's'}
                </div>
              </button>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
